<template>
  <ERow id="upload-panel" no-gutters justify="center">
    <ECol class="ma-2">
      <v-text-field
        v-if="uploadType === assetTypeOf360.Route"
        v-model="targetPointsCount"
        label="Target point count"
        type="number"
        step="1"
        class="mx-2"
        outlined
        dense
      />
      <FileBox
        :allowedTypes="allowedTypes"
        :disabled="
          ingestFileUploaderStore.uploadStats &&
          ingestFileUploaderStore.uploadStats.isUploading
        "
      />
      <v-checkbox v-model="isTimelapse" dense>
        <template #label>
          <span class="caption"> Timelapse </span>
        </template>
      </v-checkbox>
      <v-card-actions class="my-2">
        <v-spacer />
        <v-btn
          color="white grey--text"
          elevation="0"
          :disabled="
            ingestFileUploaderStore.uploadStats &&
            ingestFileUploaderStore.uploadStats.isUploading
          "
          @click="clearForm()"
        >
          Clear
        </v-btn>
        <v-btn
          color="primary"
          :disabled="
            areFilesEmpty ||
            (ingestFileUploaderStore.uploadStats &&
              ingestFileUploaderStore.uploadStats.isUploading)
          "
          @click="processUpload"
        >
          Upload
        </v-btn>
      </v-card-actions>
    </ECol>
  </ERow>
</template>

<script>
import {
  ALLOWED_IMAGES_TYPES,
  ALLOWED_ROUTE_TYPES,
  ALLOWED_MODEL_TYPES,
  AUDIO_TYPES,
  INSTA_360_TYPES,
} from "@evercam/shared/constants/ingest"
import FileBox from "@/components/ingest/FileBox"
import * as tus from "tus-js-client/dist/tus.min.js"
import { _360AssetType } from "@evercam/shared/types"
import { IngestApi } from "@evercam/shared/api/ingestApi"
import { mapStores } from "pinia"
import { useAccountStore } from "@/stores/account"
import { useIngestFileUploaderStore } from "@/stores/ingestFileUploader"

export default {
  components: {
    FileBox,
  },
  props: {
    uploadType: {
      type: String,
      default: "",
    },
    id: {
      type: Number,
      required: true,
    },
  },
  data: () => ({
    uploadedUrls: [],
    assetTypeOf360: _360AssetType,
    targetPointsCount: 0,
    isTimelapse: false,
  }),
  computed: {
    ...mapStores(useAccountStore, useIngestFileUploaderStore),
    allowedTypes() {
      const allowedTypes = {
        [_360AssetType.Image]: ALLOWED_IMAGES_TYPES,
        [_360AssetType.Model]: ALLOWED_MODEL_TYPES,
        [_360AssetType.Route]: ALLOWED_ROUTE_TYPES,
        [_360AssetType.Video]: INSTA_360_TYPES,
        [_360AssetType.MobilePhoto]: ALLOWED_IMAGES_TYPES,
        [_360AssetType.MobileAudio]: AUDIO_TYPES,
      }

      return allowedTypes[this.uploadType] || []
    },
    areFilesEmpty() {
      return this.ingestFileUploaderStore.files.length === 0
    },
  },
  mounted() {
    this.ingestFileUploaderStore.selectedFileType = "videos"
    this.ingestFileUploaderStore.files = []
  },
  methods: {
    async processUpload() {
      if (this.areFilesEmpty) {
        this.$notifications.error("Please provide files to upload")

        return
      }

      try {
        this.ingestFileUploaderStore.uploadStats.isUploading = true
        this.ingestFileUploaderStore.uploadStats.percentage = 0
        for (let i = 0; i < this.ingestFileUploaderStore.files.length; i++) {
          this.uploadedUrls.push(
            await this.uploadFile(this.ingestFileUploaderStore.files[i])
          )
        }
        this.uploadToPortal()
      } catch (e) {
        this.$notifications.error(`Couldn't upload files : ${e}`)
      }
    },
    async uploadFile(file) {
      return new Promise((resolve, reject) => {
        const upload = new tus.Upload(file, {
          endpoint: `${this.$config.public.portalURL}`,
          chunkSize: Infinity,
          retryDelays: [0, 1000, 3000, 5000],
          parallelUploads: 1,
          metadata: {
            filename: file.name,
            filetype: file.type,
          },
          onProgress: (bytesUploaded) => {
            this.ingestFileUploaderStore.uploadStats.percentage = (
              ((this.ingestFileUploaderStore.uploadStats.uploadedSize +
                bytesUploaded) /
                this.ingestFileUploaderStore.uploadStats.totalSize) *
              100
            ).toFixed(2)
          },
          onSuccess: () => {
            this.ingestFileUploaderStore.uploadStats.uploadedSize += file.size
            this.ingestFileUploaderStore.uploadStats.percentage = (
              (this.ingestFileUploaderStore.uploadStats.uploadedSize /
                this.ingestFileUploaderStore.uploadStats.totalSize) *
              100
            ).toFixed(2)

            file.uploadPercentage = "100"
            const lastDotIndex = upload.file.name.lastIndexOf(".")
            const title = upload.file.name.substring(0, lastDotIndex)
            const fileExtension = upload.file.name.substring(lastDotIndex + 1)
            resolve({
              url: upload.url,
              title,
              fileExtension,
            })
          },
          onError: (error) => {
            console.log("Failed because: " + error)
            reject(error)
          },
        })
        upload.findPreviousUploads().then((previousUploads) => {
          // Found previous uploads so we select the first one.
          if (previousUploads.length) {
            upload.resumeFromPreviousUpload(previousUploads[0])
          }

          // Start the upload
          upload.start()
        })
      })
    },
    uploadToPortal() {
      this.ingestFileUploaderStore.uploadStats.isProcessing = true
      this.ingestFileUploaderStore.uploadStats.isUploading = false
      IngestApi.assets360
        .upload(
          this.id,
          { uploadedBy: this.accountStore.email },
          {
            assetType: this.uploadType,
            upload: this.uploadedUrls,
            targetPointsCount: this.targetPointsCount,
            isTimelapse: this.isTimelapse,
          }
        )
        .then(() => {
          this.ingestFileUploaderStore.uploadStats = {
            percentage: 0,
            totalSize: 0,
            uploadedSize: 0,
            isUploading: false,
            isProcessing: false,
          }
          this.uploadedUrls = []
          this.$emit("upload-completed")
        })
        .catch((e) => {
          this.$notifications.error("Error uploading images", e)
          this.uploadedUrls = []
        })

      return
    },
    clearForm() {
      this.ingestFileUploaderStore.files = []
      this.floor = ""
      this.date = ""
    },
  },
}
</script>

<style lang="scss">
#upload-panel {
  z-index: 2 !important;
}
</style>
